import { useState } from "react";
import { X, MessageSquare, Mail, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { track } from "@/lib/analytics";

const formatRand = (n) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n || 0);

export default function QuoteDialog({ part, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please add your name");
      return;
    }
    if (!phone.trim() && !email.trim()) {
      toast.error("Add a phone or email so the supplier can reach you");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/quote", {
        part_id: part.id,
        buyer_name: name,
        buyer_phone: phone || null,
        buyer_email: email || null,
        message,
      });
      setResult(data);
      track("generate_lead", {
        currency: "ZAR",
        value: part.price_zar || 0,
        item_id: part.id,
        item_name: part.name,
        make: part.make,
        model: part.model,
        supplier: part.supplier_name,
        email_sent: !!data.email_sent,
      });
      if (data.email_sent) {
        toast.success("Email sent to supplier");
      } else if (data.supplier_email) {
        toast.success("Quote logged. Use WhatsApp link below to chat directly.");
      } else {
        toast.success("Quote logged");
      }
    } catch (err) {
      toast.error("Could not submit quote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="quote-dialog"
      className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-neutral-50 border border-neutral-200 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono-tech text-[#FF5100]">
              CONTACT SUPPLIER
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">
              {part.name}
            </h2>
            <div className="text-xs text-neutral-500 mt-0.5">
              {part.make} {part.model} · {part.engine} · {formatRand(part.price_zar)} at {part.supplier_name}
            </div>
          </div>
          <button data-testid="quote-close-button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
            <Field label="Your name *">
              <input
                data-testid="quote-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
                placeholder="e.g. Mike Mech"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Phone">
                <input
                  data-testid="quote-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
                  placeholder="+27 ..."
                />
              </Field>
              <Field label="Email">
                <input
                  data-testid="quote-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
                  placeholder="you@workshop.co.za"
                />
              </Field>
            </div>
            <Field label="Message (optional)">
              <textarea
                data-testid="quote-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 py-2 text-sm"
                placeholder="Anything specific about fitment, delivery, etc."
              />
            </Field>
            <button
              type="submit"
              data-testid="quote-submit"
              disabled={busy}
              className="w-full px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : "Send Enquiry"}
            </button>
          </form>
        ) : (
          <div className="space-y-3" data-testid="quote-result">
            <div className="border border-emerald-700/50 bg-emerald-900/10 p-3 text-sm text-emerald-300">
              {result.email_sent
                ? `Email sent to ${result.supplier_email}. The supplier will reply directly.`
                : result.supplier_email
                ? `Quote logged. Reach the supplier directly:`
                : `Quote logged. Reach the supplier directly:`}
            </div>

            {result.whatsapp_link && (
              <a
                data-testid="quote-whatsapp-link"
                href={result.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 h-11 bg-[#25D366] hover:bg-[#1EBE5D] text-black font-bold uppercase tracking-wider text-xs transition-colors"
              >
                <MessageSquare size={14} /> Chat on WhatsApp
              </a>
            )}

            {result.supplier_email && (
              <a
                data-testid="quote-email-link"
                href={`mailto:${result.supplier_email}`}
                className="w-full flex items-center justify-center gap-2 px-4 h-11 border border-neutral-200 hover:border-neutral-300 text-neutral-900 text-xs uppercase tracking-wider font-bold transition-colors"
              >
                <Mail size={14} /> {result.supplier_email}
              </a>
            )}

            {result.supplier_phone && (
              <a
                data-testid="quote-phone-link"
                href={`tel:${result.supplier_phone}`}
                className="w-full flex items-center justify-center gap-2 px-4 h-11 border border-neutral-200 hover:border-neutral-300 text-neutral-900 text-xs uppercase tracking-wider font-bold transition-colors"
              >
                <Phone size={14} /> {result.supplier_phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

export const partImageUrl = (path) => `${API}/files/${path}`;
